/**
 * Central formatters — locale-aware money / numbers / dates.
 *
 * NUMERAL RULE (user mandate):
 *   Numbers MUST always render in Latin digits (0-9), even when the UI
 *   language is Arabic or "Both". Every Intl formatter below is pinned
 *   to `numberingSystem: 'latn'` to enforce this contract.
 *
 *   • formatCurrency(123456.78)        →  "SAR 123,456.78"        (lang=en)
 *                                          "123,456.78 ر.س"        (lang=ar)
 *                                          "SAR 123,456.78"        (lang=both)
 *   • formatDate('2026-06-04')         →  "Jun 04, 2026" / "04 يونيو 2026"
 */
import i18n from '../i18n';

const ARABIC = 'ar';

function getLang() {
  // Bilingual ("both") mode uses LTR + English layout, so numbers + dates
  // follow the English locale. Pure Arabic mode flips to Arabic strings
  // but the numerals are still latin (numberingSystem hint below).
  let stored = '';
  try { stored = localStorage.getItem('app_lang') || ''; } catch { /* ignore */ }
  if (stored === 'ar') return ARABIC;
  if (stored === 'en' || stored === 'both') return 'en';
  const lng = (i18n.language || i18n.resolvedLanguage || 'en').toLowerCase();
  return lng.startsWith('ar') ? ARABIC : 'en';
}

/**
 * Format a number using locale rules.
 * @param {number|string} value
 * @param {object}        opts  { minimumFractionDigits, maximumFractionDigits }
 */
export function formatNumber(value, opts = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const lang = getLang();
  const localeTag = lang === ARABIC ? 'ar-SA' : 'en-US';
  const {
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
  } = opts;
  return new Intl.NumberFormat(localeTag, {
    minimumFractionDigits, maximumFractionDigits,
    numberingSystem: 'latn',
  }).format(n);
}

/**
 * Format a monetary amount.
 * Arabic   → "123,456.78 ر.س"   (suffix, Latin digits)
 * English  → "SAR 123,456.78"   (prefix, Latin digits)
 */
export function formatCurrency(value, opts = {}) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const lang = getLang();
  const { currency = 'SAR' } = opts;

  if (lang === ARABIC) {
    const num = new Intl.NumberFormat('ar-SA', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
      numberingSystem: 'latn',
    }).format(n);
    const suffix = currency === 'SAR' ? 'ر.س' : currency;
    return `${num} ${suffix}`;
  }
  const num = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
    numberingSystem: 'latn',
  }).format(n);
  return `${currency} ${num}`;
}

/**
 * Parse a date-ish value into a Date safely (strings/Date both work).
 */
function _toDate(value) {
  if (value instanceof Date) return value;
  if (typeof value === 'string' && value) {
    // Accept "YYYY-MM-DD" and full ISO strings.
    const d = new Date(value.length === 10 ? `${value}T00:00:00` : value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Format a date — "04 يونيو 2026" / "Jun 04, 2026".
 * Pass { withTime: true } for time as well.
 */
export function formatDate(value, opts = {}) {
  const d = _toDate(value);
  if (!d) return '';
  const lang = getLang();
  const localeTag = lang === ARABIC ? 'ar-SA' : 'en-US';
  const { withTime = false } = opts;
  if (lang === ARABIC) {
    const datePart = new Intl.DateTimeFormat('ar-SA', {
      day: '2-digit', month: 'long', year: 'numeric',
      numberingSystem: 'latn',
    }).format(d);
    if (!withTime) return datePart;
    const time = new Intl.DateTimeFormat('ar-SA', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      numberingSystem: 'latn',
    }).format(d);
    return `${datePart} — ${time}`;
  }
  const datePart = new Intl.DateTimeFormat(localeTag, {
    day: '2-digit', month: 'short', year: 'numeric',
    numberingSystem: 'latn',
  }).format(d);
  if (!withTime) return datePart;
  const time = new Intl.DateTimeFormat(localeTag, {
    hour: '2-digit', minute: '2-digit',
    numberingSystem: 'latn',
  }).format(d);
  return `${datePart} ${time}`;
}

/**
 * Short relative-ish format for tables — same as formatDate but locked to numeric.
 * "04/06/2026"
 */
export function formatDateShort(value) {
  const d = _toDate(value);
  if (!d) return '';
  const lang = getLang();
  return new Intl.DateTimeFormat(lang === ARABIC ? 'ar-SA' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    numberingSystem: 'latn',
  }).format(d);
}
